import React from 'react'
import { format, NumberFormatOptions, unformat, useNumberFormat } from '@react-input/number-format'

import { Input, InputProps } from '../input/input'

const OPTIONS: NumberFormatOptions & { locales?: Intl.LocalesArgument } = {
    locales: 'ru-RU',
    format: 'currency',
    currency: 'RUB',
    signDisplay: 'never',
    maximumFractionDigits: 0,
}

const OPTIONS_WITH_FRACTION_DIGITS: NumberFormatOptions & { locales?: Intl.LocalesArgument } = {
    ...OPTIONS,
    maximumFractionDigits: 2,
}

export interface InputMoneyProps extends Omit<InputProps, 'onChange'> {
    onChange: (value: number) => void
    withFractionDigits?: boolean
}

export const InputMoney: React.FC<InputMoneyProps> = React.forwardRef<
    HTMLInputElement,
    InputMoneyProps
>(({ onChange, value, withFractionDigits = false, ...props }, ref) => {
    const defaultValue =
        typeof value === 'number'
            ? format(value, withFractionDigits ? OPTIONS_WITH_FRACTION_DIGITS : OPTIONS)
            : null
    const inputRef = useNumberFormat(withFractionDigits ? OPTIONS_WITH_FRACTION_DIGITS : OPTIONS)
    const [formattedValue, setFormattedValue] = React.useState<string | null>(defaultValue)

    React.useEffect(() => {
        if (value) {
            setFormattedValue(
                format(value, withFractionDigits ? OPTIONS_WITH_FRACTION_DIGITS : OPTIONS)
            )
        }
    }, [value, withFractionDigits])

    React.useEffect(() => {
        if (!ref) {
            return
        }

        if (typeof ref === 'function') {
            ref(inputRef.current)
        } else {
            ref.current = inputRef.current
        }
    }, [inputRef, ref])

    return (
        <Input
            ref={inputRef}
            autoComplete='off'
            inputMode='numeric'
            type='text'
            {...props}
            value={formattedValue}
            onChange={(event) => {
                const { value } = event.target
                const number = Number(unformat(value, 'ru-RU'))
                setFormattedValue(value)
                onChange(number)
            }}
            onPaste={(event) => {
                event.preventDefault()
                event.stopPropagation()

                const value = event.clipboardData.getData('text')
                const ceiledValue = Math.ceil(Number(value.replaceAll(/[^\d,.]/g, '')))

                setFormattedValue(
                    format(ceiledValue, withFractionDigits ? OPTIONS_WITH_FRACTION_DIGITS : OPTIONS)
                )
                onChange(ceiledValue)
            }}
        />
    )
})

InputMoney.displayName = 'InputMoney'
